<template>
  <section :class="[
    `card bg-${variant} border-transparent widget-group`,
    {
      'widget-group-stretched': align === 'stretch'
    },
    `mb-${marginBottom}`
  ]">
    <div class="card-body">
      <slot name="header">
        <header class="media mb-3">
          <div class="media-left" slot="header-title">
            <div :class="`${iconSize === 'lg' ? 'h1' : 'h6'} mr-3`">
              <i :class="`fa fa-fw ${icon} text-${iconVariant}`"></i>
            </div>
          </div>
          <div class="media-body" slot="header-body">
            <div class="media">
              <div class="media-body">
                <div
                  v-if="!explore"
                  :class="`h6 text-uppercase text-${textVariant}`"
                  v-html="title"
                ></div>
                <a
                  v-if="explore"
                  :href="exploreLink"
                  :class="`h6 d-inline-block text-uppercase text-${textVariant}`"
                  v-html="title"
                ></a>
                <div
                  :class="`text-small pb-0 ${textVariant}`"
                >
                  <slot name="description">
                    <div v-html="description"></div>
                  </slot>
                </div>
                <div
                  v-if="!hideFilters"
                  :class="`h5 pb-0 text-${textVariant}`"
                >
                  <slot name="filters">
                    <b-badge
                      v-for="(filter, i) in displayFilters"
                      :key="`filter_${i}`"
                      :href="filter.readonly ? null : '#filters'"
                      :class="[
                        'mr-1',
                        {
                          'filter-readonly': filter.readonly
                        }
                      ]"
                      :variant="badgeVariant"
                      @click.prevent="!filter.readonly ? showFilters(filter) : null"
                    >
                      {{ filter.display }}
                      <i
                        v-if="!filter.readonly"
                        class="fa fa-pencil ml-1"
                      ></i>
                    </b-badge>
                  </slot>
                </div>
              </div>
              <div
                v-if="explore"
                class="media-right"
              >
                <a
                  :href="exploreLink"
                  :class="`btn btn-sm btn-${exploreVariant}`"
                >
                  {{ exploreLabel }}
                </a>
              </div>
            </div>
          </div>
        </header>
      </slot>
      <div :class="[
        'row',
        {
          'align-items-stretch': align === 'stretch'
        }
      ]">
        <slot></slot>
      </div>
      <slot name="footer"></slot>
    </div>
  </section>
</template>

<style lang="scss">
@media screen and (max-width: 640px) {
  .widget-group {
    margin-left: -15px;
    margin-right: -15px;
    border-radius: 0;
  }
}

.widget-group-stretched {
  .widget-content {
    padding-bottom: 50px;
  }
  .widget-footer {
    position: absolute;
    bottom: 1.25rem;
    left: 1.25rem;
    right: 1.25rem;
  }
}

.widget-group {
  .badge {
    &.filter-readonly {
      opacity: 0.80;
    }

    .fa-pencil {
      display: none;
    }

    &:hover {
      .fa-pencil {
        display: inline-block;
      }
    }
  }
}
</style>

<script>
import moment from 'moment'
import { PRESET_RANGES, isSame, formatDate } from '@/lib/moment'
import { uniqBy, values, filter, map, flatMapDeep, findKey, merge } from 'lodash'
import { mapState } from 'vuex'

export default {
  props: {
    title: {
      type: String,
      default: null
    },
    marginBottom: {
      type: [String, Number],
      default: 4
    },
    description: {
      type: String,
      default: null
    },
    align: {
      type: String,
      default: null
    },
    variant: {
      type: String,
      default: 'flat-dark-clouds'
    },
    iconVariant: {
      type: String,
      default: 'flat-darker-clouds'
    },
    badgeVariant: {
      type: String,
      default: null
    },
    exploreVariant: {
      type: String,
      default: 'flat-darker-clouds'
    },
    textVariant: {
      type: String,
      default: 'body'
    },
    iconSize: {
      type: String,
      default: 'lg'
    },
    icon: {
      type: String,
      default: 'fa-object-group'
    },
    explore: {
      type: String,
      default: null
    },
    exploreLabel: {
      type: String,
      default: 'Explore'
    },
    hideFilters: {
      type: Boolean,
      default: false
    },
    filters: {
      type: Array,
      default () {
        return []
      }
    }
  },
  data () {
    return {
      widgets: {},
      exploreLink: null,
      displayFilters: []
    }
  },
  computed: {
    ...mapState('dashboard', [
      'filterValues'
    ])
  },
  watch: {
    filterValues: {
      deep: true,
      handler () {
        this.updateExploreLink()
      }
    }
  },
  mounted () {
    this.$on('widget.config.updated', (widget) => {
      this.widgets[widget._uid] = widget.filters
      this.onUpdateFilters()
    })

    this.$on('widget.config.loading', (widget) => {
      this.widgets[widget._uid] = {}
      this.onUpdateFilters()
    })

    this.updateExploreLink()
    this.onUpdateFilters()
  },
  methods: {
    updateExploreLink () {
      if (this.explore) {
        let url = new URL(this.explore)
        let filters = Object.assign({}, this.filterValues)
        if (url.searchParams.has('filter')) {
          let overrides = JSON.parse(atob(url.searchParams.get('filter')))
          filters = merge(filters, overrides)
        }
        url.searchParams.set('filter', btoa(JSON.stringify(filters)))
        this.exploreLink = url
      }
    },
    onUpdateFilters () {
      let filtersByWidget = values(this.widgets)

      let filtersToDisplay = filter(flatMapDeep(filtersByWidget, (filters) => {
        return map(filters, (filter) => {
          let display = null;
          // if server gives us a display value, display it
          if (filter.display !== null) {
            display = filter.display
          // if the value is a date range, parse it through preset labels
          } else if (filter.value && filter.component === 'DateRange') {
            let from = moment(filter.value.from)
            let to = moment(filter.value.to)
            display = findKey(PRESET_RANGES, range =>
              isSame(range[0], from.toDate(), 'day') &&
              isSame(range[1], to.toDate(), 'day')
            )
            if (display === undefined) {
              let format = 'MMM Do YYYY'
              display = formatDate(from, format) + ' - ' + formatDate(to, format)
            }
          }
          if (display) {
            return {
              type: filter.filter,
              readonly: filter.readonly,
              display
            };
          } else {
            return null
          }
        })
      }))

      this.displayFilters = uniqBy(merge(filtersToDisplay, this.filters), (filter) => {
        return filter.type + ':' + filter.display
      })
    },

    showFilters (filter) {
      this.$parent.$emit('filters.show', filter)
    }
  }
}
</script>